import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, FileUp, MessagesSquare, X } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Badge, Button } from '../../components/ui';
import { STAT_CARD_BASE } from '../../components/ui/StatCard';
import MicroSimUploadPanel from '../../components/admin/MicroSimUploadPanel';
import adminMicroSimService from '../../services/adminMicroSimService';
import { showError, showSuccess } from '../../utils/toast';

/**
 * Super-admin: the micro simulation catalogue. Every topic with its published and draft
 * versions; upload a new topic or a new version (Excel template or JSON). A published
 * topic appears in every library immediately — no deploy.
 */
const AdminMicroSimsPage = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      setTopics((await adminMicroSimService.listTopics()).topics || []);
    } catch (err) {
      showError(err.message || 'Could not load the catalogue');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saved = (version) => {
    showSuccess(`Saved ${version.title} v${version.version} as a draft — preview it, then publish.`);
    navigate(`/admin/micro-sims/${version.slug}`);
  };

  return (
    <DashboardLayout title="Micro simulations">
      <div className="mx-auto w-full max-w-6xl space-y-5 px-4 py-6 lg:px-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Micro simulations</h2>
            <p className="mt-1 max-w-2xl text-sm text-neutral-500">
              Topics and versions. Upload a topic from the Excel template, preview it, then publish — it
              appears in every library straight away.
            </p>
          </div>
          <Button variant={uploading ? 'secondary' : 'primary-filled'} onClick={() => setUploading(!uploading)}>
            {uploading ? <><X size={15} className="mr-1.5" /> Close</> : <><FileUp size={15} className="mr-1.5" /> Upload topic</>}
          </Button>
        </div>

        {uploading && (
          <div className={`${STAT_CARD_BASE} p-5`}>
            <MicroSimUploadPanel onSaved={saved} />
          </div>
        )}
        {loading ? (
          <div className={`${STAT_CARD_BASE} h-40 animate-pulse`} />
        ) : topics.length === 0 ? (
          <div className={`${STAT_CARD_BASE} px-6 py-14 text-center`}>
            <MessagesSquare className="mx-auto text-neutral-300" size={28} />
            <p className="mt-3 text-sm font-semibold text-neutral-900 dark:text-neutral-100">No topics yet</p>
            <p className="mt-1 text-xs text-neutral-500">Upload one, or run <code>npm run seed:microsims</code> to load Message Matters.</p>
          </div>
        ) : (
          <div className={`${STAT_CARD_BASE} divide-y divide-neutral-100 dark:divide-neutral-800`}>
            {topics.map((t) => (
              <button
                key={t.slug}
                type="button"
                onClick={() => navigate(`/admin/micro-sims/${t.slug}`)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-neutral-50 dark:hover:bg-neutral-800/50"
              >
                <span className="min-w-0">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{t.title}</span>
                    {t.publishedVersion
                      ? <Badge variant="success" size="sm">Published v{t.publishedVersion}</Badge>
                      : <Badge variant="default" size="sm">Not published</Badge>}
                    {t.hasDraft && <Badge variant="warning" size="sm">Draft</Badge>}
                  </span>
                  <span className="mt-0.5 block text-xs text-neutral-500">
                    {t.slug} · {t.theme} · {t.versions} version{t.versions === 1 ? '' : 's'} · {t.launches} launch{t.launches === 1 ? '' : 'es'}
                  </span>
                </span>
                <ChevronRight size={16} className="shrink-0 text-neutral-400" />
              </button>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminMicroSimsPage;
